/**
 * The sign-in page. Kicks off the OAuth 2.1 + PKCE login when the button is
 * pressed. (The already-signed-in redirect happens in the entry file before
 * this renders, so a returning user never sees this page flash.)
 */

import { useState } from 'react';
import { beginLogin } from '../oauth';
import { API_URL_OPTIONS } from '../environment';

const CUSTOM_ENVIRONMENT = 'custom';

export function LoginPage() {
  const [disabled, setDisabled] = useState(false);
  const [environment, setEnvironment] = useState<string>(API_URL_OPTIONS[0]);
  const [customApiUrl, setCustomApiUrl] = useState('');
  const [status, setStatus] = useState<{ text: string; className: string }>({ text: '', className: 'status' });

  const onClick = async () => {
    setDisabled(true);
    setStatus({ text: 'Opening Kobiton sign-in…', className: 'status' });

    // Open synchronously from the click so browser popup protection allows it.
    // PKCE setup may make a network request before the authorization URL is ready.
    const width = 520;
    const height = 720;
    const left = Math.max(0, window.screenX + (window.outerWidth - width) / 2);
    const top = Math.max(0, window.screenY + (window.outerHeight - height) / 2);
    const popup = window.open(
      '',
      'kobiton-oauth',
      `popup=yes,width=${width},height=${height},left=${Math.round(left)},top=${Math.round(top)}`,
    );

    if (!popup) {
      setDisabled(false);
      setStatus({
        text: 'The sign-in popup was blocked. Allow popups for this site and try again.',
        className: 'status error',
      });
      return;
    }

    const closedCheck = window.setInterval(() => {
      if (!popup.closed) return;
      window.clearInterval(closedCheck);
      setDisabled(false);
      setStatus({ text: 'The sign-in window was closed.', className: 'status' });
    }, 500);

    try {
      await beginLogin(environment === CUSTOM_ENVIRONMENT ? customApiUrl : environment, popup);
      setStatus({ text: 'Finish signing in in the popup…', className: 'status' });
    } catch (error) {
      window.clearInterval(closedCheck);
      if (!popup.closed) popup.close();
      setDisabled(false);
      setStatus({ text: error instanceof Error ? error.message : String(error), className: 'status error' });
    }
  };

  return (
    <main className="card">
      <div className="brand">
        <span className="brand-dot" /> Kobiton Quickportal
      </div>
      <h1>Sign in with Kobiton</h1>
      <p className="sub">
        Kobiton authentication opens in a popup. We use OAuth 2.1 with PKCE — no passwords ever touch this app.
      </p>
      <label className="environment-field" htmlFor="environment">
        <span>Environment</span>
        <select
          id="environment"
          value={environment}
          onChange={(event) => setEnvironment(event.target.value)}
          disabled={disabled}
        >
          {API_URL_OPTIONS.map((url) => (
            <option value={url} key={url}>
              {url}
            </option>
          ))}
          <option value={CUSTOM_ENVIRONMENT}>Custom API URL…</option>
        </select>
      </label>
      {environment === CUSTOM_ENVIRONMENT && (
        <label className="environment-field custom-environment-field" htmlFor="custom-api-url">
          <span>Custom API URL</span>
          <input
            id="custom-api-url"
            value={customApiUrl}
            onChange={(event) => setCustomApiUrl(event.target.value)}
            disabled={disabled}
            autoComplete="url"
            spellCheck={false}
            placeholder="api.example.com"
            required
            autoFocus
          />
        </label>
      )}
      <button id="login" onClick={onClick} disabled={disabled}>
        Sign in with Kobiton
      </button>
      <div className={status.className} role="status" aria-live="polite">
        {status.text}
      </div>
    </main>
  );
}
